import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import stationApi from '../../../../utils/api/location-management';

interface ChartDataProperties {
  name: string;
  value: number;
  color: string;
}

interface DeviceStatsState {
  totalDevices: number;
  onlineDeviceCount: number;
  offlineDeviceCount: number;
  availableDeviceCount: number;
  unavailableDeviceCount: number;
}

/**
 * Colors for chart
 * @param {string} status The status string
 * @returns {string} The color string
 */
function getStatusColor(status: string) {
  const rootStyles = getComputedStyle(document.documentElement);
  const onlineColor = rootStyles.getPropertyValue('--online-color');
  const offlineColor = rootStyles.getPropertyValue('--offline-color');
  const availableColor = rootStyles.getPropertyValue('--available-color');
  const unAvailableColor = rootStyles.getPropertyValue('--unavailable-color');
  const chargerColor = rootStyles.getPropertyValue('--charging-color');
  const faultedColor = rootStyles.getPropertyValue('--faulted-color');
  const unknownColor = rootStyles.getPropertyValue('--unknown-color');
  switch (status.toLowerCase()) {
    case 'online':
      return onlineColor;
    case 'offline':
      return offlineColor;
    case 'available':
      return availableColor;
    case 'unavailable':
      return unAvailableColor;
    case 'charging':
      return chargerColor;
    case 'faulted':
      return faultedColor;
    default:
      return unknownColor;
  }
}

/**
 * Station status distribution chart component
 * @returns {any} The rendered component
 */
function LocationStatusDistributionChart() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { selectedLocationPerformanceList, selectedChargingPoint } = useAppSelector(
    (state) => state.locationManagement,
  );

  const [deviceStats, setDeviceStats] = useState<DeviceStatsState>({
    totalDevices: 0,
    onlineDeviceCount: 0,
    offlineDeviceCount: 0,
    availableDeviceCount: 0,
    unavailableDeviceCount: 0,
  });

  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   * Function for fetching station device status count report
   */
  const fetchStationDeviceStatusCount = async () => {
    const stationId = selectedLocationPerformanceList?.id || selectedChargingPoint?.id;
    if (!stationId) return;

    setLoading(true);
    try {
      const response = await stationApi.getStationDeviceStatusCount({
        chargingStation: stationId,
      });

      if (response?.status === 200 || response?.data) {
        const deviceData = response.data || response;

        const stats: DeviceStatsState = {
          totalDevices: deviceData.total ?? 0,
          onlineDeviceCount: deviceData.online ?? 0,
          offlineDeviceCount: deviceData.offline ?? 0,
          availableDeviceCount: deviceData.available ?? 0,
          unavailableDeviceCount: deviceData.unavailable ?? 0,
        };
        setDeviceStats(stats);

        const chartData: ChartDataProperties[] = [
          {
            name: 'Online',
            value: stats.onlineDeviceCount,
            color: getStatusColor('Online'),
          },
          {
            name: 'Offline',
            value: stats.offlineDeviceCount,
            color: getStatusColor('Offline'),
          },
          {
            name: 'Available',
            value: stats.availableDeviceCount,
            color: getStatusColor('Available'),
          },
          {
            name: 'Unavailable',
            value: stats.unavailableDeviceCount,
            color: getStatusColor('Unavailable'),
          },
        ];
        setBarGraphData(chartData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Function for tooltip
   * @param {any} props The tooltip props
   * @param {boolean} props.active Whether the tooltip is active
   * @param {any[]} props.payload The tooltip payload
   * @returns {any} The rendered tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div
          className="connector-status-custom-tooltip"
          style={{ '--status-color': data?.color || data?.payload?.color } as React.CSSProperties}>
          <p className="label">{`${t(data.name.toLowerCase(), data.name)} : ${data.value}`}</p>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    fetchStationDeviceStatusCount();
  }, [selectedLocationPerformanceList?.id, selectedChargingPoint?.id]);

  const activeChartData = barGraphData.filter((item) => item.value > 0);

  return (
    <div className="location-status-distributionChart-wrap-report" dir={i18n.dir()}>
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <span className="title-text">
              {t('stationStatusOverview', 'Station Status Overview')}
            </span>
            <p className="subtitle-text">
              {t('stationStatusOverviewDesc', 'Overview of all station statuses in the system')}
            </p>
          </div>
        </div>
        <div className="energy-report-contain">
          {loading ? (
            <center className="loader-button">
              <BatteryLoader />
            </center>
          ) : (
            <>
              <div className="piechart-container">
                <PieChart
                  width={250}
                  height={250}
                  margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <Pie
                    data={activeChartData.length > 0 ? activeChartData : barGraphData}
                    cx={125}
                    cy={125}
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    isAnimationActive={false}>
                    {(activeChartData.length > 0 ? activeChartData : barGraphData).map((entry) => (
                      <Cell key={entry.name} fill={String(entry.color)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>

                <div className="total-sessions-wrap">
                  <p>{t('totalCount', 'Total')}</p>
                  <span>{deviceStats.totalDevices}</span>
                </div>
              </div>

              <div className="PieChart-heading">
                <div className="pie-chart-label">
                  {[
                    {
                      label: 'Online',
                      value: deviceStats.onlineDeviceCount,
                      color: getStatusColor('Online'),
                    },
                    {
                      label: 'Offline',
                      value: deviceStats.offlineDeviceCount,
                      color: getStatusColor('Offline'),
                    },
                    {
                      label: 'Available',
                      value: deviceStats.availableDeviceCount,
                      color: getStatusColor('Available'),
                    },
                    {
                      label: 'Unavailable',
                      value: deviceStats.unavailableDeviceCount,
                      color: getStatusColor('Unavailable'),
                    },
                  ].map((item) => (
                    <div key={item.label} className="each-label">
                      <div
                        className="each-label-color"
                        style={{ '--label-color': item.color } as React.CSSProperties}
                      />
                      <p>{t(item.label.toLowerCase(), item.label)}</p>
                      <div className="each-label-value">
                        <span>{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationStatusDistributionChart;
